import { useSnackbar } from "notistack";
import { useContext } from "react";

import { useLabels } from "api/services/useLabels";
import { BoundingBoxesContext } from "context/boundingBoxesContext";
import { useAppDispatch } from "hooks/store";
import { setUnsavedAnnotations } from "store/slices/FocusModal/focusModalSlice";
import { StepsDataProps, ValidBoxLabelsProps } from "types/boundingBoxesContextTypes";
import { CanvasAnnotation } from "types/imageAnnotations";
import { ImageQuery, REVIEW_REASON_CHOICES } from "types/imageQueries";
import { isImageQueryInQueue } from "utils/imageQueriesUtils";
import { createArrayRois, getBackendLabel, mapLabels } from "utils/ReviewPageUtils";

export const useSaveAnnotations = (
  annotations: CanvasAnnotation[],
  validBoxLabels: ValidBoxLabelsProps[],
  stepsData: StepsDataProps,
  imageQuery?: ImageQuery
) => {
  const { createLabelAsync, isCreatingLabel } = useLabels();
  const contextValue = useContext(BoundingBoxesContext);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const mappedLabels = mapLabels(annotations, validBoxLabels, stepsData);

  const saveAnnotations = async (label?: string, canSave: boolean = false) => {
    if (!imageQuery) return;

    if (isImageQueryInQueue(imageQuery) && !canSave) {
      enqueueSnackbar("Cannot save annotations for images without an answer. Please add a Label", { variant: "error" });
      return;
    }

    let rois: CanvasAnnotation[] | undefined;
    if (annotations) {
      rois = annotations;
    } else if (contextValue) {
      const { getValidBoxes } = contextValue;
      const validBoxes = getValidBoxes();
      const arrayAnnotations = createArrayRois(validBoxes);
      rois = arrayAnnotations;
    }
    const labelValue = label || getBackendLabel(imageQuery.current_best_answer_str || "");
    await createLabelAsync({
      posicheck_id: imageQuery.iq_id,
      label: labelValue,
      review_reason: REVIEW_REASON_CHOICES.CUSTOMER_INITIATED,
      rois,
    });
    enqueueSnackbar("Annotations saved successfully.", { variant: "success" });
    dispatch(setUnsavedAnnotations(false));
  };

  return {
    saveAnnotations,
    loadingAddLabel: isCreatingLabel,
    mappedLabels,
  };
};
